import { standardSaleOpen } from "@/config/secureMacStorefront";
import { consumerApi } from "./consumerApi";
import { SECUREMAC_PRO_CATALOG_KEY } from "@/config/secureMacProStorefront";
import {
  FORTRESS_PRO_ADDON_CATALOG_KEY,
  HALO_ENTITLEMENT_CODE,
  fortressProAddonCatalogEnabled,
  haloSaleOpen,
} from "@/config/fortressStorefront";

/**
 * Consumer-platform contracts used by the website client for SecureShield Pro,
 * SecureAI and (release-gated) Halo / Fortress.
 * Schemas mirror lightningflow-consumer-platform CatalogResponse / CheckoutRedirect /
 * EntitlementsResponse / DownloadAuthorizationResponse — keep
 * consumerCommerce.test.js green when either side changes.
 */

/** Platform password floor (identity/routes.py MIN_PASSWORD_LENGTH). */
export const CONSUMER_MIN_PASSWORD_LENGTH = 14;
export const SECUREAI_CATALOG_KEY = "secureai";

function requireHttpsUrl(value, label) {
  const url = String(value || "");
  if (!url.startsWith("https://")) {
    throw new Error(`${label} URL missing or not HTTPS`);
  }
  return url;
}

export function pickSecureMacProCatalogProduct(catalog) {
  const products = Array.isArray(catalog?.products) ? catalog.products : [];
  return products.find((product) => product?.key === SECUREMAC_PRO_CATALOG_KEY) || null;
}

export async function fetchConsumerCatalog() {
  const { data } = await consumerApi.get("/v1/catalog");
  return data;
}

const ENTITLEMENT_STATUSES = new Set(["none", "active", "revoked", "expired"]);

/**
 * Authoritative SecureShield Pro entitlement state. Never inferred from release
 * endpoints: a paying customer must not see "Buy" just because no build is
 * published yet.
 */
export function pickSecureMacProEntitlement(response) {
  const items = Array.isArray(response?.entitlements) ? response.entitlements : [];
  const found = items.find((item) => item?.product === SECUREMAC_PRO_CATALOG_KEY);
  if (!found || !ENTITLEMENT_STATUSES.has(found.status)) {
    return { product: SECUREMAC_PRO_CATALOG_KEY, status: "none", active: false };
  }
  return { ...found, active: found.active === true && found.status === "active" };
}

export async function fetchSecureMacProEntitlement() {
  const { data } = await consumerApi.get("/v1/entitlements");
  return pickSecureMacProEntitlement(data);
}

export async function startSecureMacProCheckout({ platform = "macos" } = {}) {
  if (!["macos", "windows"].includes(platform)) throw new Error("Unknown download platform");
  const route = `/v1/products/${SECUREMAC_PRO_CATALOG_KEY}/checkout`;
  const { data } = platform === "windows" ? await consumerApi.post(route, { platform }) : await consumerApi.post(route);
  // Consumer platform returns { url, session_id }; never follow http.
  const url = requireHttpsUrl(data?.url, "Checkout");
  return { url, session_id: data?.session_id || "" };
}

export async function fetchSecureMacProLatestRelease() {
  const { data } = await consumerApi.get("/v1/releases/securemac-pro/latest");
  return data;
}

export async function authorizeSecureMacProDownload(releaseId) {
  const { data } = await consumerApi.get(`/v1/releases/${releaseId}/download`);
  const url = data?.download_url || "";
  if (!String(url).startsWith("https://")) {
    throw new Error("Download URL missing or not HTTPS");
  }
  if (/lightningflow\.ai\/securemac/i.test(url) || /#plans/i.test(url)) {
    throw new Error("Download URL must not be a marketing page");
  }
  return data;
}

export async function downloadSecureMacProRelease() {
  const latest = await fetchSecureMacProLatestRelease();
  return authorizeSecureMacProDownload(latest.release_id);
}

/** Thrown before any network call when a sale is closed. */
export class ConsumerSaleClosedError extends Error {
  constructor(message, code = "sale_closed") {
    super(message);
    this.name = "ConsumerSaleClosedError";
    this.code = code;
  }
}

export const HALO_CHECKOUT_PLANS = Object.freeze(["monthly", "annual"]);

/** Consumer-platform `halo` entitlement (the Halo subscription). Fails closed. */
export function pickHaloEntitlement(response) {
  const items = Array.isArray(response?.entitlements) ? response.entitlements : [];
  const found = items.find((item) => item?.product === HALO_ENTITLEMENT_CODE);
  if (!found || !ENTITLEMENT_STATUSES.has(found.status)) {
    return { product: HALO_ENTITLEMENT_CODE, status: "none", active: false };
  }
  return { ...found, active: found.active === true && found.status === "active" };
}

/**
 * Halo SuperVPN subscription checkout (website Stripe). Fortress is the
 * same subscription. Refuses — without calling the API — unless the source
 * release approval is on AND the catalog reports both Halo plans verified.
 */
export async function startHaloCheckout({ plan, catalog } = {}) {
  if (!HALO_CHECKOUT_PLANS.includes(plan)) throw new Error("Unknown Halo plan");
  if (!haloSaleOpen(catalog)) throw new ConsumerSaleClosedError("Halo sales are closed");
  const { data } = await consumerApi.post("/v1/products/halo/checkout", { plan });
  return { url: requireHttpsUrl(data?.url, "Checkout"), session_id: data?.session_id || "" };
}

/** Fortress base edition: the Halo subscription. */
export function startFortressCheckout(options) {
  return startHaloCheckout(options);
}

/**
 * Fortress Pro add-on: US$11.99 once for a customer with an active Halo
 * subscription. The consumer platform applies the Halo-subscriber coupon to the
 * SecureShield Pro product server-side; the browser never sends a price or coupon.
 */
export async function startFortressProAddonCheckout({ catalog, haloEntitlement, platform = "macos" } = {}) {
  if (!haloSaleOpen(catalog) || !fortressProAddonCatalogEnabled(catalog)) {
    throw new ConsumerSaleClosedError("Fortress Pro sales are closed");
  }
  if (!(haloEntitlement?.active === true && haloEntitlement.status === "active")) {
    throw new ConsumerSaleClosedError("Fortress Pro needs an active Halo subscription", "halo_required");
  }
  if (!["macos", "windows"].includes(platform)) throw new Error("Unknown download platform");
  const route = `/v1/products/${FORTRESS_PRO_ADDON_CATALOG_KEY}/checkout`;
  const { data } = platform === "windows" ? await consumerApi.post(route, { platform }) : await consumerApi.post(route);
  return { url: requireHttpsUrl(data?.url, "Checkout"), session_id: data?.session_id || "" };
}

export async function startSecureAICheckout() {
  const { data } = await consumerApi.post(`/v1/products/${SECUREAI_CATALOG_KEY}/checkout`);
  return {
    url: requireHttpsUrl(data?.url, "Checkout"),
    session_id: data?.session_id || "",
  };
}

export async function downloadSecureAIRelease() {
  const { data: latest } = await consumerApi.get("/v1/releases/secureai/latest");
  const { data } = await consumerApi.get(`/v1/releases/${latest?.release_id}/download`);
  return { ...data, download_url: requireHttpsUrl(data?.download_url, "Download") };
}

export async function openStripeBillingPortal() {
  const { data } = await consumerApi.post("/v1/billing/portal");
  return { url: requireHttpsUrl(data?.url, "Billing portal") };
}

export async function fetchSecureAIDevices() {
  const { data } = await consumerApi.get("/v1/devices", {
    params: { product: SECUREAI_CATALOG_KEY },
  });
  return Array.isArray(data?.devices) ? data.devices : [];
}

export async function releaseSecureAIDevice(deviceId) {
  await consumerApi.delete(`/v1/devices/${deviceId}`);
}

export async function verifyConsumerEmail(token) {
  await consumerApi.post("/v1/identity/verify-email", { token });
}

export async function requestConsumerPasswordReset(email) {
  await consumerApi.post("/v1/identity/request-reset", { email });
}

export async function completeConsumerPasswordReset(token, password) {
  await consumerApi.post("/v1/identity/complete-reset", { token, password });
}

/** Read a one-time token from the URL fragment (#token=...), never the query. */
export function readFragmentToken(hash) {
  const raw = String(hash || "").replace(/^#/, "");
  const token = new URLSearchParams(raw).get("token");
  return token && token.length >= 16 ? token : "";
}

/** Map consumer-platform errors to customer-facing copy. */
export function consumerErrorMessage(err, fallback = "Something went wrong. Please try again.") {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (!err?.response) return "We couldn't reach LightningFlow. Check your connection and try again.";
  if (status === 429) return "Too many attempts. Please wait a few minutes and try again.";
  if (status === 401) {
    return detail === "Invalid credentials"
      ? "That email and password don't match."
      : "Your session has ended. Sign in again.";
  }
  if (status === 403 && detail === "Email verification required") {
    return "Verify your email first — check your inbox for the link.";
  }
  if (status === 403 && detail === "CSRF validation failed") {
    return "Your session needs refreshing. Reload the page and try again.";
  }
  if (status === 403 && detail === "Active SecureShield Pro entitlement required") {
    return "This account doesn't have an active SecureShield Pro licence.";
  }
  if (status === 409 && detail === "Halo subscription is already active") {
    return "You already have Halo SuperVPN. Fortress uses the same subscription.";
  }
  if (status === 404 && detail === "No release published") {
    return "Your download isn't ready yet. Check back soon — your purchase is safe.";
  }
  if (status === 422) return "Check the details you entered and try again.";
  if (status === 503 && typeof detail === "string" && /SecureAI/i.test(detail)) {
    return "SecureAI isn't available right now. Please try again later.";
  }
  if (status === 503) return "This product isn't available right now. Please try again later.";
  if (typeof detail === "string" && detail.length < 160) return detail;
  return fallback;
}

export async function startSecureShieldCheckout(catalog, { platform = "macos" } = {}) {
  if (!["macos", "windows"].includes(platform)) throw new Error("Unknown download platform");
  if (!standardSaleOpen(catalog)) throw new ConsumerSaleClosedError("SecureShield sales are closed");
  const route = "/v1/products/secureshield/checkout";
  const { data } = platform === "windows" ? await consumerApi.post(route, { platform }) : await consumerApi.post(route);
  return { url: requireHttpsUrl(data?.url, "Checkout"), session_id: data?.session_id || "" };
}
export async function downloadSecureShieldRelease() {
  const { data: latest } = await consumerApi.get("/v1/releases/secureshield/latest");
  if (!latest?.release_id) throw new Error("No standard release published");
  return authorizeSecureMacProDownload(latest.release_id);
}
